import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { FadeUp, MaskReveal, Stagger, StaggerItem, ease } from "@/components/Motion";
import CounselCallout from "@/components/CounselCallout";
import useSEO, { canonicalFor } from "@/lib/useSEO";
import CalendlyButton from "@/components/CalendlyButton";
import ProfitTeaser from "@/components/tools/ProfitTeaser";
import ToolsGrid from "@/components/tools/ToolsGrid";
import { TOOL_PATHS } from "@/config/tools";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/d6795f3d2d8957a22a2d44bb7cab186f58e3af4a35b7d231a5a6732c6ca5ce52.png";
const MOOD_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/f5d1c19c073c283d5186002cbe4b6da0df54ddc76efa652eb9e0b79ec571dd2b.png";

const VALUE_NS = ["I", "II", "III", "IV"];
const SERVICE_NS = ["01", "02", "03", "04", "05", "06"];
// Service index -> matching free tools (formation, relocation, immigration, tax & compliance)
const SERVICE_TOOLS = { 1: ["setup"], 3: ["roadmap"], 4: ["roadmap"], 5: ["calendar", "profit"] };

export default function Home() {
  const { t } = useTranslation();
  useSEO({
    title: "Corpora Consultants — Corporate law counsel for the global operator",
    description: "Cyprus-based corporate law practice. Legal, regulatory and tax architecture for international operators — CySEC, MiCA, MiFID II, Mauritius, Seychelles, Canada MSB. Confidential counsel from senior lawyers.",
    canonical: canonicalFor("/"),
    image: canonicalFor("/brand/corpora-og.jpg"),
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.15]);

  const [insights, setInsights] = useState([]);
  useEffect(() => {
    api
      .get("/insights", { params: { limit: 3 } })
      .then((r) => setInsights(r.data.slice(0, 3)))
      .catch(() => {});
  }, [t]);

  const services = t("home.services", { returnObjects: true });
  const values = t("home.values", { returnObjects: true });
  const testimonials = t("home.testimonials", { returnObjects: true });
  const marquee = t("home.marquee", { returnObjects: true });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative min-h-screen overflow-hidden grain"
      >
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="absolute inset-0"
        >
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.42) contrast(1.08) grayscale(0.18)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,11,13,0.55) 0%, rgba(10,11,13,0.4) 35%, rgba(10,11,13,0.97) 100%)",
            }}
          />
        </motion.div>

        <div className="absolute bottom-8 ltr:right-8 ltr:md:right-12 rtl:left-8 rtl:md:left-12 z-10 hidden md:flex items-center gap-3">
          <span className="live-dot" />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[var(--bone-2)]">
            {t("home.acceptingMandates")}
          </span>
        </div>

        <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end min-h-screen">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease }}
              className="flex items-center gap-4 mb-10"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[var(--pewter)]">
                {t("home.established")}
              </span>
              <span className="w-12 h-px bg-[var(--line-2)]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[var(--bone-2)]">
                {t("tools.hero.eyebrow")}
              </span>
            </motion.div>

            <h1
              data-testid="hero-title"
              className="display text-[var(--bone)]"
              style={{ fontSize: "clamp(46px, 6.6vw, 112px)" }}
            >
              <span className="block leading-[0.95]">
                <MaskReveal delay={0.1}>{t("tools.hero.line1")}</MaskReveal>
              </span>
              <span className="block leading-[0.95] display-italic mt-2" style={{ color: "var(--pewter)" }}>
                <MaskReveal delay={0.35}>{t("tools.hero.line2")}</MaskReveal>
              </span>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8, ease }}
              className="mt-10"
            >
              <p className="text-[var(--bone-2)] text-base md:text-xl leading-relaxed font-light max-w-xl">
                {t("tools.hero.intro")}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <CalendlyButton data-testid="hero-cta-primary" className="btn-primary">
                  {t("tools.book")} <ArrowRight size={14} className="rtl:rotate-180" />
                </CalendlyButton>
                <a href="#free-tools" data-testid="hero-cta-secondary" className="btn-secondary">
                  {t("tools.hero.explore")}
                </a>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.6, ease }}
            className="lg:col-span-5"
          >
            <ProfitTeaser />
          </motion.div>
        </div>
      </section>

      {/* FREE TOOLS */}
      <section id="free-tools" className="section-pad scroll-mt-24" data-testid="free-tools-section">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-6">
              <FadeUp>
                <div className="overline mb-6">{t("tools.section.eyebrow")}</div>
                <h2 className="display text-5xl md:text-6xl lg:text-7xl text-[var(--bone)]">
                  {t("tools.section.title1")}
                  <br />
                  <span className="display-italic" style={{ color: "var(--pewter)" }}>{t("tools.section.title2")}</span>
                </h2>
              </FadeUp>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <FadeUp delay={0.15}>
                <p className="text-[var(--bone-2)] text-base md:text-lg leading-relaxed">{t("tools.section.lead")}</p>
              </FadeUp>
            </div>
          </div>
          <ToolsGrid />
        </div>
      </section>

      {/* TICKER */}
      <section className="border-y border-[var(--line)] py-5 overflow-hidden bg-[var(--ink-2)]">
        <div className="marquee-track font-serif text-[var(--bone-2)] text-2xl md:text-3xl whitespace-nowrap"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 300' }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex items-center gap-12">
              {marquee.map((m, j) => (
                <span key={j} className="flex items-center gap-12">
                  <span className="italic">{m}</span>
                  <span style={{ color: "var(--pewter)" }}>·</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </section>

      {/* PRACTICE */}
      <section className="section-pad" data-testid="what-we-do">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <FadeUp>
              <div className="overline mb-6">{t("home.section1Eyebrow")}</div>
              <h2 className="display text-5xl md:text-6xl lg:text-7xl text-[var(--bone)]">
                {t("home.section1Title1")}
                <br />
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("home.section1Title2")}
                </span>
              </h2>
            </FadeUp>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <FadeUp delay={0.2}>
              <p
                className="font-serif text-2xl md:text-3xl leading-[1.4] text-[var(--bone)]"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 300', fontWeight: 300 }}
                dangerouslySetInnerHTML={{ __html: t("home.section1Lead") }}
              />
              <p className="mt-8 text-[var(--mute)] leading-[1.8] max-w-xl">
                {t("home.section1Body")}
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="max-w-[1480px] mx-auto px-6 md:px-10 mt-24">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)]">
            {services.map((s, i) => (
              <StaggerItem
                key={i}
                className="svc-card bg-[var(--ink)] p-10 min-h-[300px] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between">
                  <span className="svc-num font-mono text-[var(--mute-2)] text-sm tracking-[0.22em]">
                    {SERVICE_NS[i]}
                  </span>
                  <ArrowUpRight size={18} className="text-[var(--mute-2)]" />
                </div>
                <div>
                  <h3 className="display text-3xl md:text-4xl text-[var(--bone)] leading-[1.05]">
                    {s.title}
                  </h3>
                  <p className="mt-5 text-[var(--mute)] leading-relaxed">
                    {s.body}
                  </p>
                  {SERVICE_TOOLS[i] && (
                    <div className="mt-6 flex flex-col gap-2">
                      {SERVICE_TOOLS[i].map((tid) => (
                        <Link
                          key={tid}
                          to={TOOL_PATHS[tid]}
                          data-testid={`service-${i}-tool-${tid}`}
                          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)] hover:text-[var(--bone)] transition-colors"
                        >
                          {t(`tools.cards.${tid}.title`)} <ArrowRight size={12} className="rtl:rotate-180" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CounselCallout />

      {/* PRINCIPLES */}
      <section
        className="section-pad relative overflow-hidden border-t border-[var(--line)]"
        style={{ background: "var(--ink-2)" }}
        data-testid="values-section"
      >
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="overline mb-6">{t("home.section2Eyebrow")}</div>
              <h2 className="display text-5xl md:text-6xl lg:text-7xl text-[var(--bone)]">
                {t("home.section2Title1")}
                <br />
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("home.section2Title2")}
                </span>
              </h2>
              <p className="mt-8 text-[var(--mute)] max-w-md leading-relaxed">
                {t("home.section2Lead")}
              </p>
            </FadeUp>
          </div>

          <div className="md:col-span-7">
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-14">
              {values.map((v, i) => (
                <StaggerItem key={i} className="border-t border-[var(--line)] pt-7">
                  <div
                    className="display-italic font-serif"
                    style={{
                      fontSize: "92px",
                      color: "var(--pewter)",
                      lineHeight: 0.85,
                      fontVariationSettings: '"opsz" 144, "SOFT" 50, "wght" 300',
                      fontWeight: 300,
                    }}
                  >
                    {VALUE_NS[i]}
                  </div>
                  <h3 className="mt-4 display text-2xl md:text-3xl text-[var(--bone)]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-[var(--mute)] leading-relaxed">
                    {v.body}
                  </p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section className="relative h-[64vh] min-h-[440px] overflow-hidden">
        <img
          src={MOOD_IMG}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "brightness(0.55) grayscale(0.2)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,11,13,0.35) 0%, rgba(10,11,13,0.85) 100%)",
          }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1480px] w-full mx-auto px-6 md:px-10 pb-16">
            <FadeUp>
              <p className="display text-3xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-[var(--bone)]">
                {t("home.imageBreak1")}{" "}
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("home.imageBreakEdge")}
                </span>{" "}
                {t("home.imageBreak2")}{" "}
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("home.imageBreakCentre")}
                </span>{" "}
                {t("home.imageBreak3")}
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad" data-testid="testimonials-section">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <FadeUp>
            <div className="overline mb-10">{t("home.section3Eyebrow")}</div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)]">
            {testimonials.map((tt, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="bg-[var(--ink)] p-10 h-full flex flex-col justify-between min-h-[340px]">
                  <div className="seal mb-8">“</div>
                  <p
                    className="display text-2xl md:text-[26px] leading-[1.35] text-[var(--bone)]"
                    style={{ fontWeight: 300 }}
                  >
                    {tt.quote}
                  </p>
                  <div className="mt-8 overline">{tt.who}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section className="section-pad border-t border-[var(--line)]" data-testid="insights-preview">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <FadeUp>
              <div className="overline mb-4">{t("home.section4Eyebrow")}</div>
              <h2 className="display text-5xl md:text-6xl text-[var(--bone)]">
                {t("home.section4Title1")}{" "}
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("home.section4TitleEU")}
                </span>{" "}
                {t("home.section4Title2")}
              </h2>
            </FadeUp>
            <Link to="/insights" data-testid="view-all-insights" className="btn-ghost">
              {t("home.viewJournal")} <ArrowRight size={14} />
            </Link>
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {insights.map((p) => (
              <StaggerItem key={p.id} className="insight-card group">
                <Link to={`/insights/${p.slug}`} className="block">
                  <div className="overflow-hidden aspect-[4/3] mb-6">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="insight-img w-full h-full object-cover"
                    />
                  </div>
                  <div className="overline mb-3">{p.category}</div>
                  <h3 className="display text-2xl md:text-3xl text-[var(--bone)] leading-[1.1] group-hover:text-[var(--pewter)] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[var(--mute)] leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* NEXUS BAND — sub-brand teaser */}
      <section className="home-nexus-band section-pad" data-testid="home-nexus-band">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="grid gap-12 md:grid-cols-12 items-end">
            <div className="md:col-span-7">
              <div className="overline text-[var(--pewter)] mb-6">Corpora Nexus · Marketplace</div>
              <h2 className="display text-[var(--bone)]" style={{ fontSize: "clamp(44px, 6vw, 88px)", lineHeight: 1.02 }}>
                A curated marketplace for <em style={{ color: "var(--pewter)", fontStyle: "italic" }}>regulated</em> & <em style={{ color: "var(--pewter)", fontStyle: "italic" }}>intangible</em> assets.
              </h2>
              <p className="mt-8 text-[var(--bone-2)] text-lg max-w-2xl leading-relaxed">
                Financial licences · business stakes · IP · fund structures · iGaming platforms. Confidential, NDA-gated,
                with Corpora Consultants as your counsel through the transaction.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/nexus" data-testid="home-nexus-cta" className="btn-primary inline-flex">
                  Explore Nexus <ArrowRight size={14} />
                </Link>
                <Link to="/nexus/sell" className="link-under text-[var(--bone-2)] hover:text-[var(--bone)] font-mono text-[11px] uppercase tracking-[0.22em]">
                  List an asset →
                </Link>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="border border-[var(--line-2)] p-8">
                <div className="overline text-[var(--pewter)] mb-4">Live marketplace</div>
                <ul className="space-y-4 text-[var(--bone-2)] text-sm">
                  <li className="flex items-baseline justify-between border-b border-[var(--line)] pb-3">
                    <span>CySEC CIF (MiFID II + MiCA)</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">Featured</span>
                  </li>
                  <li className="flex items-baseline justify-between border-b border-[var(--line)] pb-3">
                    <span>Mauritius IDL · Full Service</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone-2)]/70">Active</span>
                  </li>
                  <li className="flex items-baseline justify-between border-b border-[var(--line)] pb-3">
                    <span>MSB · Freshly approved (valid to 2030)</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone-2)]/70">Available</span>
                  </li>
                  <li className="flex items-baseline justify-between">
                    <span>Cyprus STP Brokerage · Multi-banking</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone-2)]/70">Available</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-[var(--line)] flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone-2)]/60">13 active · 6 jurisdictions</span>
                  <Link to="/nexus/listings" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--pewter)] hover:text-[var(--bone)]">
                    Browse all →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad" data-testid="home-cta">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="border-t border-[var(--line)] pt-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-7">
              <h2
                className="display text-[var(--bone)]"
                style={{ fontSize: "clamp(56px, 9vw, 150px)" }}
              >
                {t("home.uncomplicated")}
              </h2>
              <p className="mt-8 text-[var(--bone-2)] text-lg max-w-xl leading-relaxed">
                {t("home.ctaFinal")}
              </p>
            </div>
            <div className="md:col-span-5 md:flex md:justify-end">
              <Link to="/contact" data-testid="cta-contact" className="btn-primary">
                {t("home.ctaFinalBtn")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
